import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { articleBySlug } from "../lib/content";
import { passageBySlug } from "../lib/passage";
import {
  type Prayer,
  prayerBySlug,
  prayerMinutes,
  prayerOutline,
} from "../lib/prayers";
import { verseTopicBySlug } from "../lib/verses";
import { formatDate } from "./ArticleBody";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * One written prayer.
 *
 * ── Why the prayer comes before the explanation ──────────────────────────────
 *
 * Because it is what the reader came for, and because of when they came. The
 * queries this page answers are typed by somebody who is already in the
 * situation — at night, before the conversation, after the argument — and every
 * competing page makes them scroll past six paragraphs about the importance of
 * prayer to reach the words. The takeaways and the prayer are above everything
 * that explains them, for the same reason `VerseCollection` puts the passage
 * above its note.
 *
 * ── Why the prayer is not styled like scripture ──────────────────────────────
 *
 * The navy-and-left-rule treatment on this site means "these are the words of
 * the Bible" (docs/DESIGN-SYSTEM.md §9.2). A written prayer is not that, and
 * dressing it in the same clothes would be a small lie told in CSS. It gets a
 * card of its own — ink border, surface fill — and the passages further down
 * keep the scripture treatment, so the difference between the two is visible
 * without anyone having to say it.
 */

function Spoken({ lines, short }: { lines: string[]; short: string }) {
  return (
    <section className="mt-10">
      <h2
        id="the-prayer"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        The prayer
      </h2>
      <div className="brutal mt-6 p-6 sm:p-8">
        {lines.map((line) => (
          <p
            key={line.slice(0, 48)}
            className="mt-4 font-body text-[clamp(1.05rem,2.4vw,1.2rem)] leading-[1.8] text-body first:mt-0"
          >
            {line}
          </p>
        ))}
      </div>
      <p className="mt-5 text-sm leading-relaxed text-ink2">
        <span className="font-display font-bold text-ink">
          If there is no time for that:
        </span>{" "}
        {short}
      </p>
    </section>
  );
}

function When({ lines }: { lines: string[] }) {
  return (
    <section className="mt-10">
      <h2
        id="when-to-pray-this"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        When to pray this
      </h2>
      <ul className="mt-5 flex flex-col gap-3">
        {lines.map((line) => (
          <li
            key={line.slice(0, 48)}
            className="border-l-[3px] border-brand pl-4 leading-[1.75] text-body"
          >
            {line}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Honest({ lines }: { lines: string[] }) {
  return (
    <section className="mt-12">
      <h2
        id="what-this-prayer-is-not"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        What this prayer is not
      </h2>
      {lines.map((line) => (
        <p key={line.slice(0, 48)} className="mt-4 leading-[1.75] text-body">
          {line}
        </p>
      ))}
    </section>
  );
}

function Passages({ picks }: { picks: Prayer["verses"] }) {
  return (
    <section className="mt-12">
      <h2
        id="the-passages"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        What it leans on
      </h2>
      <ol className="mt-6 flex flex-col gap-5">
        {picks.map((pick) => (
          <li key={pick.verse.ref} className="brutal p-5 sm:p-6">
            <p className="font-display text-sm font-bold tracking-wide text-scripture uppercase">
              {pick.verse.ref}
            </p>
            <figure className="mt-4 border-l-[3px] border-scripture bg-scripture-wash/60 py-4 pr-4 pl-5">
              <blockquote className="font-display text-[clamp(1.05rem,2.5vw,1.25rem)] leading-snug text-scripture">
                “{pick.verse.text}”
              </blockquote>
            </figure>
            <p className="mt-4 leading-[1.75] text-body">{pick.note}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function CardShell({
  heading,
  children,
}: {
  heading: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-bold text-ink">{heading}</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">{children}</ul>
    </section>
  );
}

const CARD =
  "brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5";
const CARD_TITLE = "font-display text-lg leading-snug font-bold text-ink";
const CARD_TEXT = "text-sm leading-relaxed text-ink2";

/**
 * Four near-identical link lists rather than one generic component.
 *
 * TanStack's `Link` is typed on the literal route path — `to` and `params` are
 * checked against each other — so a shared card that took `to: string` would
 * have to throw that away with a cast, and the cast is the only thing standing
 * between a renamed route and a dead link on every prayer page. Repetition is
 * cheaper than that, and it is the shape `VerseCollection` already uses.
 */
function MorePrayers({ slugs }: { slugs: string[] }) {
  const items = slugs.map(prayerBySlug).filter((p) => p !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="More prayers">
      {items.map((item) => (
        <li key={item.slug}>
          <Link
            to="/prayers/$slug"
            params={{ slug: item.slug }}
            className={CARD}
          >
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

function Collections({ slugs }: { slugs: string[] }) {
  const items = slugs.map(verseTopicBySlug).filter((t) => t !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Collections">
      {items.map((item) => (
        <li key={item.slug}>
          <Link
            to="/verses/$slug"
            params={{ slug: item.slug }}
            className={CARD}
          >
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

function RelatedPassages({ slugs }: { slugs: string[] }) {
  const items = slugs.map(passageBySlug).filter((p) => p !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Passages">
      {items.map((item) => (
        <li key={item.slug}>
          <Link to="/verse/$slug" params={{ slug: item.slug }} className={CARD}>
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

function ReadNext({ slugs }: { slugs: string[] }) {
  const items = slugs.map(articleBySlug).filter((a) => a !== undefined);
  if (items.length === 0) return null;
  return (
    <CardShell heading="Read next">
      {items.map((item) => (
        <li key={item.slug}>
          <Link to="/blog/$slug" params={{ slug: item.slug }} className={CARD}>
            <span className={CARD_TITLE}>{item.h1}</span>
            <span className={CARD_TEXT}>{item.description}</span>
          </Link>
        </li>
      ))}
    </CardShell>
  );
}

export function PrayerPage({ prayer }: { prayer: Prayer }) {
  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(prayer.published)}
        {prayer.updated ? ` · updated ${formatDate(prayer.updated)}` : ""} ·{" "}
        {prayerMinutes(prayer)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {prayer.h1}
      </h1>
      <p className="mt-4 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {prayer.standfirst}
      </p>

      <ShortAnswer takeaways={prayer.takeaways} />
      <Contents
        headings={[
          { id: "the-prayer", text: "The prayer" },
          { id: "when-to-pray-this", text: "When to pray this" },
          { id: "the-passages", text: "What it leans on" },
          ...prayerOutline(prayer),
          { id: "what-this-prayer-is-not", text: "What this prayer is not" },
        ]}
      />

      <Spoken lines={prayer.prayer} short={prayer.short} />
      <When lines={prayer.when} />
      <Passages picks={prayer.verses} />

      {prayer.body.map(renderBlock)}

      <Honest lines={prayer.honest} />

      {prayer.faq ? <FaqList items={prayer.faq} /> : null}

      <StoreCta from={`prayer:${prayer.slug}`} />

      <MorePrayers slugs={prayer.relatedPrayers ?? []} />
      <Collections slugs={prayer.relatedTopics ?? []} />
      <RelatedPassages slugs={prayer.relatedPassages ?? []} />
      <ReadNext slugs={prayer.related} />
    </article>
  );
}
